import React from 'react';

/**
 * SectionWrapper wraps individual sections of the resume.
 * Sets boundaries for DOM measurements used by the PageEngine.
 *
 * dividerStyle — controls how section titles are displayed:
 *   'line'     (default) — uppercase title + bottom border (original behavior, no regression)
 *   'left-bar' — colored 3px left accent bar beside the title
 *   'pill'     — title inside a rounded colored pill label
 *   'full-bar' — full-width colored background bar behind title
 *   'dots'     — title with a dotted line extending to the right
 *   'none'     — title text only, no decorative divider
 */
export const SectionWrapper = ({ id, title, children, canSplit = true, dividerStyle = 'line', style = {} }) => {
  const renderTitle = () => {
    if (!title) return null;

    // ── line (default) — preserves 100% of existing template appearance ──────
    if (dividerStyle === 'line' || !dividerStyle) {
      return (
        <h2
          className="section-title"
          style={{
            fontSize: 'var(--font-size-heading, 9.5pt)',
            fontFamily: 'var(--font-family-primary)',
            color: 'var(--color-primary)',
            borderBottom: 'var(--border-thickness-heading, 1.5px) solid var(--color-primary)',
            paddingBottom: '3px',
            marginBottom: 'var(--spacing-paragraph-gap, 6px)',
            textTransform: 'var(--heading-text-transform, uppercase)',
            letterSpacing: 'var(--heading-letter-spacing, 0.05em)',
            fontWeight: 'var(--heading-weight, 700)',
            breakAfter: 'avoid',
            pageBreakAfter: 'avoid',
            pageBreakInside: 'avoid',
            breakInside: 'avoid',
          }}
        >
          {title}
        </h2>
      );
    }

    // ── left-bar — colored 3px accent bar on the left of the title ───────────
    if (dividerStyle === 'left-bar') {
      return (
        <div
          className="section-title"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: 'var(--spacing-paragraph-gap, 6px)',
            breakAfter: 'avoid',
            pageBreakAfter: 'avoid',
          }}
        >
          <div
            style={{
              width: '3px',
              height: '14px',
              backgroundColor: 'var(--color-primary)',
              borderRadius: '2px',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 'var(--font-size-heading, 9.5pt)',
              fontFamily: 'var(--font-family-primary)',
              color: 'var(--color-primary)',
              textTransform: 'var(--heading-text-transform, uppercase)',
              letterSpacing: 'var(--heading-letter-spacing, 0.05em)',
              fontWeight: 'var(--heading-weight, 700)',
            }}
          >
            {title}
          </span>
          <div
            style={{
              flex: 1,
              height: '1px',
              backgroundColor: 'var(--color-border, #E2E8F0)',
            }}
          />
        </div>
      );
    }

    // ── pill — rounded colored label pill ────────────────────────────────────
    if (dividerStyle === 'pill') {
      return (
        <div
          className="section-title"
          style={{
            marginBottom: 'var(--spacing-paragraph-gap, 6px)',
            breakAfter: 'avoid',
            pageBreakAfter: 'avoid',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              fontSize: 'var(--font-size-heading, 9.5pt)',
              fontFamily: 'var(--font-family-primary)',
              color: 'var(--color-primary)',
              backgroundColor: 'color-mix(in srgb, var(--color-primary) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--color-primary) 25%, transparent)',
              borderRadius: '20px',
              padding: '2px 12px',
              textTransform: 'var(--heading-text-transform, uppercase)',
              letterSpacing: 'var(--heading-letter-spacing, 0.08em)',
              fontWeight: 'var(--heading-weight, 700)',
            }}
          >
            {title}
          </span>
        </div>
      );
    }

    // ── full-bar — full-width colored background behind the title ─────────────
    if (dividerStyle === 'full-bar') {
      return (
        <div
          className="section-title"
          style={{
            backgroundColor: 'var(--color-primary)',
            padding: '4px 10px',
            marginBottom: 'var(--spacing-paragraph-gap, 6px)',
            marginLeft: '-10px',
            marginRight: '-10px',
            breakAfter: 'avoid',
            pageBreakAfter: 'avoid',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-heading, 9.5pt)',
              fontFamily: 'var(--font-family-primary)',
              color: '#ffffff',
              textTransform: 'var(--heading-text-transform, uppercase)',
              letterSpacing: 'var(--heading-letter-spacing, 0.1em)',
              fontWeight: 'var(--heading-weight, 700)',
            }}
          >
            {title}
          </span>
        </div>
      );
    }

    // ── dots — title + dotted line extending right ────────────────────────────
    if (dividerStyle === 'dots') {
      return (
        <div
          className="section-title"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: 'var(--spacing-paragraph-gap, 6px)',
            breakAfter: 'avoid',
            pageBreakAfter: 'avoid',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-heading, 9.5pt)',
              fontFamily: 'var(--font-family-primary)',
              color: 'var(--color-primary)',
              textTransform: 'var(--heading-text-transform, uppercase)',
              letterSpacing: 'var(--heading-letter-spacing, 0.05em)',
              fontWeight: 'var(--heading-weight, 700)',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </span>
          <div
            style={{
              flex: 1,
              borderBottom: '1.5px dotted var(--color-primary)',
              opacity: 0.4,
              marginBottom: '2px',
            }}
          />
        </div>
      );
    }

    // ── none — title text only, generous whitespace, no decorative element ────
    if (dividerStyle === 'none') {
      return (
        <div
          className="section-title"
          style={{
            marginBottom: 'var(--spacing-paragraph-gap, 6px)',
            paddingTop: '4px',
            breakAfter: 'avoid',
            pageBreakAfter: 'avoid',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-heading, 9.5pt)',
              fontFamily: 'var(--font-family-primary)',
              color: 'var(--color-primary)',
              textTransform: 'var(--heading-text-transform, uppercase)',
              letterSpacing: 'var(--heading-letter-spacing, 0.08em)',
              fontWeight: 'var(--heading-weight, 700)',
            }}
          >
            {title}
          </span>
        </div>
      );
    }

    // Fallback — treat as 'line'
    return (
      <h2
        className="section-title"
        style={{
          fontSize: 'var(--font-size-heading, 9.5pt)',
          fontFamily: 'var(--font-family-primary)',
          color: 'var(--color-primary)',
          borderBottom: 'var(--border-thickness-heading, 1.5px) solid var(--color-primary)',
          paddingBottom: '3px',
          marginBottom: 'var(--spacing-paragraph-gap, 6px)',
          textTransform: 'var(--heading-text-transform, uppercase)',
          letterSpacing: 'var(--heading-letter-spacing, 0.05em)',
          fontWeight: 'var(--heading-weight, 700)',
          breakAfter: 'avoid',
          pageBreakAfter: 'avoid',
        }}
      >
        {title}
      </h2>
    );
  };

  return (
    <section
      id={`section-${id}`}
      data-render-block="true"
      data-split-allowed={canSplit ? 'true' : 'false'}
      data-divider-style={dividerStyle}
      style={{
        marginBottom: 'var(--spacing-section-mb, 16px)',
        position: 'relative',
        pageBreakInside: canSplit ? 'auto' : 'avoid',
        breakInside: canSplit ? 'auto' : 'avoid',
        // Section-level density overrides (scoped CSS variables)
        ...style
      }}
    >
      {renderTitle()}
      <div
        className="section-content"
        style={{
          marginTop: '4px',
          maxWidth: '100%',
        }}
      >
        {children}
      </div>
    </section>
  );
};
